'use client';
import { EventLocationDropdown } from '@v2/containers/EventsCarouselByLocation/EventLocationDropdown';
import { AWSLocationResponse, TeamProps } from '@v2/types';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';
import {
  EventResultsUiConfig,
  LeagueTeamFilterOption,
} from '../constants/eventPageUiTypes';
import {
  HomeAwayEventsTab,
  HomeAwayEventsTypes,
} from '../components/HomeAwayEventsTab';
import { useIsConcerts } from '../use-is-concerts';
import { OpponentsDropdownFilter } from '../components/OpponentsDropdownFilter';
import { DateRangePicker } from './DateRangePicker';
import { OponnetsDropdownWrapper } from './OponnetsDropdownWrapper';

interface Props {
  onLocationChange: (location?: AWSLocationResponse) => void;
  onDateChange: (startDate?: string, endDate?: string) => void;
  onHomeAwayChange?: (tab: HomeAwayEventsTypes) => void;
  onOpponentChange: (team?: TeamProps) => void;
  isTeamPage?: boolean;
  teamSlug?: string;
  leagueSlug?: string;
  eventResultsConfig?: EventResultsUiConfig;
  homeAwayValue?: HomeAwayEventsTypes;
  isLeaguePage?: boolean;
  leagueTeamOptions?: LeagueTeamFilterOption[];
  selectedTeamSlug?: string;
  onTeamChange?: (slug: string) => void;
}

const DEFAULT_LOCATION = {
  Text: 'Miami, FL, United States',
  PlaceId:
    'AQABAFMAP3RXXM3qdNIK2xpZw8lM2jWXLNO8qoVFkTkpp8Ik95drqyiD7WOwd1d77WbjIjCRTT_-Ghnk9t7DWJ6KrHlJlbEBCm93u4DEZjD8CanLe1GFKLOtyYWePb-zqqoKAb3WnC8XhfDJxZfaKy8rSi2Q5HPZnw',
  Categories: ['MunicipalityType'],
};

export const EventsFilters = ({
  onLocationChange,
  onDateChange,
  onHomeAwayChange,
  onOpponentChange,
  isTeamPage = false,
  teamSlug,
  leagueSlug,
  eventResultsConfig,
  homeAwayValue,
  isLeaguePage = false,
  leagueTeamOptions,
  selectedTeamSlug,
  onTeamChange,
}: Props) => {
  const [selectedLocation, setSelectedLocation] =
    useState<AWSLocationResponse>();
  const searchParams = useSearchParams();

  const opponentIdParam = searchParams?.get('opponentId');

  const isConcerts = useIsConcerts();
  const isTeamPresentation = eventResultsConfig?.presentation === 'team';
  const hideLocationFilter =
    eventResultsConfig?.hideLocationFilter ?? isTeamPresentation;
  const compactMobileFilters =
    eventResultsConfig?.compactMobileFilters ?? isTeamPresentation;

  const [selectedDateFilter, setSelectedDateFilter] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [selectedOpponentFilter, setSelectedOpponentFilter] = useState<any>();
  const leagueTeams = [
    { teamId: 'all-teams', team: 'All teams', slug: '' },
    ...(leagueTeamOptions ?? []).map((team) => ({
      teamId: team.slug,
      team: team.label,
      slug: team.slug,
    })),
  ];

  const handleChangeLocation = (location: AWSLocationResponse) => {
    onLocationChange(location);
    setSelectedLocation(location);
  };

  const onDateFilterChange = (dateFilter: DateRange) => {
    setSelectedDateFilter(dateFilter);

    const formatDate = (date?: Date) => {
      if (!date) return undefined;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    onDateChange(formatDate(dateFilter.from), formatDate(dateFilter.to));
  };

  const handleOpponentChange = (team: TeamProps) => {
    onOpponentChange(team);
    setSelectedOpponentFilter(team);
  };

  const isResetButtonVisible =
    !!selectedDateFilter.from ||
    !!selectedDateFilter.to ||
    (!hideLocationFilter && !!selectedLocation) ||
    !!selectedOpponentFilter ||
    !!selectedTeamSlug ||
    (isTeamPresentation &&
      homeAwayValue !== undefined &&
      homeAwayValue !== HomeAwayEventsTypes.ALL);

  return (
    <div
      className={`no-scrollbar flex h-auto ${
        isTeamPresentation
          ? 'flex-row justify-start overflow-visible md:mb-6'
          : compactMobileFilters
            ? 'flex-col gap-3 overflow-visible md:mb-6 md:flex-row md:items-start md:justify-between'
            : 'flex-row justify-between overflow-scroll md:mb-6 md:overflow-hidden'
      }`}
    >
      <div
        className={`${
          isTeamPresentation
            ? 'grid w-full min-w-0 grid-cols-2 gap-2 md:flex md:w-auto md:flex-wrap'
            : compactMobileFilters
              ? 'flex flex-wrap gap-3'
              : 'flex flex-row gap-x-3'
        }`}
      >
        {!isConcerts && isLeaguePage && !!leagueTeamOptions?.length && (
          <div
            className={
              isTeamPresentation ? 'col-span-2 min-w-0 md:contents' : undefined
            }
          >
            <OpponentsDropdownFilter
              teams={leagueTeams}
              selectedValue={
                selectedTeamSlug
                  ? leagueTeams.find((team) => team.slug === selectedTeamSlug)
                  : undefined
              }
              onOpponentChange={(team) => onTeamChange?.(team.slug)}
              onSearch={() => {}}
              defaultLabel="All teams"
              selectionLabel="Choose team"
              searchPlaceholder="Search teams..."
              emptyLabel="No teams found."
              compactMobile={compactMobileFilters}
            />
          </div>
        )}
        {!hideLocationFilter && (
          <div
            className={
              isTeamPresentation
                ? `${!isConcerts && isTeamPage ? 'col-span-2' : ''} min-w-0 md:w-auto [&>div]:w-full md:[&>div]:w-auto`
                : undefined
            }
          >
            <EventLocationDropdown
              selectedLocation={selectedLocation}
              variant="list"
              onLocationChange={handleChangeLocation}
              compact={isTeamPresentation}
            />
          </div>
        )}
        {isTeamPresentation &&
          !isConcerts &&
          isTeamPage &&
          onHomeAwayChange && (
            <HomeAwayEventsTab
              variant="responsive"
              value={homeAwayValue}
              onTabChange={onHomeAwayChange}
            />
          )}
        <DateRangePicker
          selectedDateFilter={selectedDateFilter}
          onDateChange={onDateFilterChange}
          defaultLabel={eventResultsConfig?.dateFilterDefaultLabel}
          compactMobile={compactMobileFilters}
          singleMonth={isTeamPresentation}
        />
        {!isConcerts && isTeamPage && !opponentIdParam && (
          <div
            className={
              isTeamPresentation ? 'col-span-2 min-w-0 md:contents' : undefined
            }
          >
            <OponnetsDropdownWrapper
              leagueSlug={leagueSlug}
              teamSlug={teamSlug}
              selectedValue={selectedOpponentFilter}
              onOpponentChange={handleOpponentChange}
              defaultLabel={eventResultsConfig?.opponentFilterDefaultLabel}
              compactMobile={compactMobileFilters}
            />
          </div>
        )}
        {isResetButtonVisible && (
          <button
            onClick={() => {
              setSelectedLocation(undefined);
              if (!isTeamPresentation || !hideLocationFilter) {
                onLocationChange(isLeaguePage ? undefined : DEFAULT_LOCATION);
              }
              onDateChange(undefined, undefined);
              setSelectedDateFilter({
                from: undefined,
                to: undefined,
              });
              if (selectedOpponentFilter) {
                setSelectedOpponentFilter(undefined);
                onOpponentChange(undefined);
              }
              if (selectedTeamSlug) onTeamChange?.('');
              if (
                isTeamPresentation &&
                isTeamPage &&
                !isConcerts &&
                onHomeAwayChange
              ) {
                onHomeAwayChange(HomeAwayEventsTypes.ALL);
              }
            }}
            className={`flex shrink-0 items-center rounded-lg text-gray-800 ${isTeamPresentation ? 'col-span-2 h-11 md:w-auto' : 'h-9'} ${
              compactMobileFilters
                ? 'px-3 text-sm md:h-12 md:px-4 md:text-base'
                : 'px-4 md:h-12'
            }`}
          >
            Reset filters
          </button>
        )}
      </div>
      {!isConcerts && isTeamPage && onHomeAwayChange && !isTeamPresentation && (
        <HomeAwayEventsTab onTabChange={onHomeAwayChange} />
      )}
    </div>
  );
};
